import type { PaymentFrequencyRequest } from '@domains/subscription/priv/types'

export interface PotentialDirtyField {
  name: string
  value: unknown
}

export interface FieldChange {
  before: PotentialDirtyField | undefined
  after: PotentialDirtyField
}

export interface BaseAlgorithm {
  type: string
}

export interface BasePrice {
  type: string
  paymentFrequency: PaymentFrequencyRequest
}

export interface ConfirmableProps<T> {
  open: boolean
  data: T
  onConfirm: (data: T) => void
  onClose: () => void
  changes: FieldChange[]
  title: string
  actionText: string
}

export type ConfirmableComponent<T> = React.ComponentType<ConfirmableProps<T>>

interface FormChangesParams {
  before: PotentialDirtyField[] | undefined
  after: PotentialDirtyField[]
  compare: (change: FieldChange) => boolean
}

export const getChangedFormFields = ({ before, after, compare }: FormChangesParams): FieldChange[] => {
  const changes = after.map((afterField) => {
    const beforeField = before?.find((field) => field.name === afterField.name)

    return { before: beforeField, after: afterField }
  })

  return changes.filter(compare)
}

export const getSortedDirtyFields = <T extends PotentialDirtyField>(data: T[]): PotentialDirtyField[] => {
  return data.toSorted((a, b) => a.name.localeCompare(b.name)).map((value) => ({ name: value.name, value: value.value }))
}

interface CreateChangeDataParams<T extends PotentialDirtyField> {
  before: T[] | undefined
  after: T[]
  isFormDirty: (change: FieldChange) => boolean
}

export const createChangesData = <T extends PotentialDirtyField>({
  before,
  after,
  isFormDirty,
}: CreateChangeDataParams<T>): FieldChange[] => {
  if (!before) return []

  return getChangedFormFields({
    before: getSortedDirtyFields(before),
    after: getSortedDirtyFields(after),
    compare: isFormDirty,
  })
}

export const normalizeFormData = <T extends PotentialDirtyField>(data: T[]): PotentialDirtyField[] => {
  return data.toSorted((a, b) => a.name.localeCompare(b.name)).map((value) => ({ name: value.name, value: value.value }))
}

interface FieldChangeFormatParams {
  changes: FieldChange[]
  formatter: { format: (name: string) => string }
}

export const formatFieldChangeName = ({ changes, formatter }: FieldChangeFormatParams): FieldChange[] => {
  return changes.map(({ before, after }) => ({
    before: before ? { name: formatter.format(before.name), value: before.value } : undefined,
    after: { name: formatter.format(after.name), value: after.value },
  }))
}
